import React, { useState, useRef, useEffect } from 'react';
import { RecordItem } from '../../models/RecordItem';
import folderIcon from '../../assets/images/folder.png';
import dotsIcon from '../../assets/images/dots.png';

interface RecordItemCardProps {
  record: RecordItem;
  onMenuClick: (event: React.MouseEvent, record: RecordItem) => void;
  onClick?: (record: RecordItem) => void;
}

const RecordItemCard: React.FC<RecordItemCardProps> = ({ record, onMenuClick, onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10 // Position above the card
      });
    }

    // Clear any existing timeouts
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);

    hoverTimeoutRef.current = setTimeout(() => {
      // Show tooltip immediately
      setShowTooltip(true);

      // Hide tooltip after 3 seconds
      tooltipTimeoutRef.current = setTimeout(() => {
        // setShowTooltip(false);
      }, 3000);
    }, 2000); // 2 second delay before showing tooltip
  };

  const handleMouseLeave = () => {
    // Clear the 3-second timeout if mouse leaves early
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // Hide tooltip after a short delay to allow moving to tooltip
    hoverTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 300);
  };

  const handleTooltipMouseEnter = () => {
    // Keep tooltip visible if hovering over it
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  };

  const handleTooltipMouseLeave = () => {
    // Hide tooltip when leaving tooltip
    setShowTooltip(false);
  };

  useEffect(() => {
    return () => {
      // Cleanup timeouts on unmount
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    };
  }, []);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  // Helper function to check if permission value is true
  const isPermissionTrue = (value: string | undefined | null): boolean => {
    if (!value) return false;
    const strValue = String(value).toLowerCase();
    return strValue === 'true' || strValue === '1' || strValue === 'yes';
  };

  // Helper function to check if user is the owner (has both read and write permissions)
  const isOwner = (record: RecordItem): boolean => {
    if (!record.permissions || record.permissions.length === 0) return false;
    
    // Check if any user has both read and write permissions
    return record.permissions.some(permission => {
      const canRead = isPermissionTrue(permission.can_read);
      const canWrite = isPermissionTrue(permission.can_write);
      return canRead && canWrite;
    });
  };

  // Get permission status text
  const getPermissionStatus = (record: RecordItem): string => {
    if (!record.permissions || record.permissions.length === 0) {
      return 'No permissions';
    }
    
    if (isOwner(record)) {
      return 'Owned';
    }
    
    return `Shared (${record.permissions.length} users)`;
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger card click if menu button was clicked
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    if (onClick) {
      onClick(record);
    }
  };

  return (
    <>
      <div
        ref={cardRef}
        className="relative flex flex-col p-4 border border-grey_light rounded-lg bg-light text-dark hover:shadow-md transition-shadow cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleCardClick}
      >
        {/* Header with folder icon and menu button */}
        <div className="flex flex-col justify-center items-center">
          <button
            onClick={(e) => onMenuClick(e, record)}
            className="absolute hover:bg-bg_grey_light right-0 p-2 rounded-full top-4"
            aria-label="Record options"
          >
            <img
              src={dotsIcon}
              alt="Menu"
              className="w-5 h-5"
            />
          </button>
          <img
            src={folderIcon}
            alt="Folder"
            className="w-16 h-16 mb-2"
          />
          <div>
            <h4 className="text-md text-center w-full">{record.record_name || 'Untitled Record'}</h4>
            <p className="text-sm text-grey_dark text-center w-full">{record.record_description || 'No description'}</p>
          </div>
        </div>

        {/* Permissions summary - always visible */}
        <div className="bg-bg_grey_light mt-3 py-2 px-4 rounded-lg text-primary w-fit mx-auto">
          <p className="text-sm text-center">
            {getPermissionStatus(record)}
          </p>
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div
          className="fixed z-50 bg-light text-dark rounded-lg border border-grey_light shadow-lg p-3 min-w-[200px] transform -translate-x-1/2 -translate-y-full"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            pointerEvents: 'auto'
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          <div className="text-[0.75rem] text-grey_dark">
            <div className="flex justify-between mb-2">
              <span className="text-grey_dark">Created:</span>
              <span className="font-medium">{formatDate(record.created_at)}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-grey_dark">Currency:</span>
              <span className="font-medium">{record.record_currency_code || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-grey_dark">Invite Code:</span>
              <span className="font-mono font-medium">{record.record_invite_code || 'N/A'}</span>
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-grey_dark">Permissions:</span>
              <span className="font-medium">{record.permissions?.length || 0} users</span>
            </div>
          </div>

          {/* Tooltip arrow */}
          <div
            className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-light"
          />
        </div>
      )}
    </>
  );
};

export default RecordItemCard;